import React from 'react';
import { Autocomplete } from '@material-ui/lab';
import OriginalTextField from '@material-ui/core/TextField';
import PropTypes from 'prop-types';
import { Button } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Teeth from './Teeth';

const LibraryAssignment = ({ libraries, teethData, update, saving }) => {
  const [selectedBracket, setSelectedBracket] = React.useState(null);

  const handleToothSelection = teeth => {
    if (!selectedBracket) {
      return;
    }
    let newTeethData = JSON.parse(JSON.stringify(teethData));
    if (teeth.length > 1) {
      const uniqueTeeth = Array.from(
        new Set([...teeth, ...newTeethData.map(t => t.tooth_number)])
      ).sort();
      newTeethData = uniqueTeeth.map(toothNumber => {
        const isSelected = teeth.includes(toothNumber);
        const existingTooth = newTeethData.find(t => t.tooth_number === toothNumber);
        return {
          tooth_number: toothNumber,
          bracket_library_id: isSelected ? selectedBracket.id : existingTooth.bracket_library_id,
          bracket_library_name: isSelected
            ? selectedBracket.name
            : existingTooth.bracket_library_name
        };
      });
    } else {
      const tooth = teeth[0];
      const existingTooth = newTeethData.find(t => t.tooth_number === tooth);
      if (existingTooth) {
        if (existingTooth.bracket_library_id === selectedBracket.id) {
          // If the tooth already has the selected bracket, remove it
          newTeethData = newTeethData.filter(t => t.tooth_number !== tooth);
        } else {
          existingTooth.bracket_library_id = selectedBracket.id;
          existingTooth.bracket_library_name = selectedBracket.name;
        }
      } else {
        newTeethData.push({
          tooth_number: tooth,
          bracket_library_id: selectedBracket.id,
          bracket_library_name: selectedBracket.name
        });
      }
    }
    // sort array by tooth_number
    newTeethData = [...newTeethData.sort((a, b) => a.tooth_number.localeCompare(b.tooth_number))];
    update(newTeethData);
  };

  const handleClear = () => {
    if (!selectedBracket) {
      return;
    }
    update(teethData.filter(tooth => tooth.bracket_library_id !== selectedBracket.id));
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <div style={{ width: 516 }}>
          <Autocomplete
            onChange={(event, value) => {
              setSelectedBracket(value);
            }}
            size="small"
            id="combo-box-demo"
            value={libraries.find(bracket => bracket.id === selectedBracket?.id) || null}
            options={libraries}
            getOptionLabel={option => option?.name}
            getOptionSelected={(option, value) => option.id === value.id}
            renderInput={params => (
              <OriginalTextField
                {...params}
                value={params.name}
                label="Select Bracket"
                variant="outlined"
              />
            )}
          />
        </div>

        <Button
          style={{ marginLeft: 8 }}
          disabled={
            !selectedBracket ||
            !teethData.filter(item => item.bracket_library_id === selectedBracket.id).length ||
            saving
          }
          onClick={handleClear}
          color="secondary"
          variant="outlined"
        >
          Clear
        </Button>
      </div>
      <Teeth
        data={teethData}
        onTeethSelection={handleToothSelection}
        onBracketLibraryClick={value => {
          setSelectedBracket(value);
        }}
      />
      <Typography
        align="center"
        style={{ marginTop: 24 }}
        variant="caption"
        display="block"
        gutterBottom
        color="textSecondary"
      >
        Hint: Hold "CTRL" (Windows) or "CMD" (Mac) key then select two teeth to assign the selected
        bracket library to the range.
      </Typography>
    </>
  );
};

LibraryAssignment.propTypes = {
  libraries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  teethData: PropTypes.arrayOf(
    PropTypes.shape({
      tooth_number: PropTypes.string.isRequired,
      bracket_library_id: PropTypes.number.isRequired
    })
  ).isRequired,
  update: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired
};

export default LibraryAssignment;
